export const USER_ROLES = {
  SUPER_ADMIN: "super_admin",
  MANAGER: "manager",
  EMPLOYEE: "employee",
  MEMBER: "member",
};

export const ROLE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: "Super Admin",
  [USER_ROLES.MANAGER]: "Manager",
  [USER_ROLES.EMPLOYEE]: "Employee",
  [USER_ROLES.MEMBER]: "Member",
};

export const ROLE_COLORS = {
  [USER_ROLES.SUPER_ADMIN]: { background: "#f5f3ff", color: "#6d28d9" },
  [USER_ROLES.MANAGER]: { background: "#eff6ff", color: "#2563eb" },
  [USER_ROLES.EMPLOYEE]: { background: "#ecfeff", color: "#0e7490" },
  [USER_ROLES.MEMBER]: { background: "#f1f5f9", color: "#475569" },
};

// Deterministic palette for custom roles created via Role Management, so
// each one gets a consistent (but distinct-looking) color across reloads.
const CUSTOM_ROLE_COLOR_PALETTE = [
  { background: "#fff7ed", color: "#c2410c" },
  { background: "#fdf4ff", color: "#a21caf" },
  { background: "#f0fdf4", color: "#15803d" },
  { background: "#fef2f2", color: "#b91c1c" },
  { background: "#eef2ff", color: "#4338ca" },
  { background: "#fefce8", color: "#a16207" },
];

const hashString = (value) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const getRoleColor = (roleName) => {
  const normalizedRole = normalizeRole(roleName);

  if (ROLE_COLORS[normalizedRole]) {
    return ROLE_COLORS[normalizedRole];
  }

  return CUSTOM_ROLE_COLOR_PALETTE[hashString(normalizedRole) % CUSTOM_ROLE_COLOR_PALETTE.length];
};

export const PAGE_ACCESS = {
  dashboard: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.MEMBER,
  ],
  tickets: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.MEMBER,
  ],
  board: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.MEMBER,
  ],
  employees: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
  ],
  departments: [
    USER_ROLES.SUPER_ADMIN,
  ],
  projects: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.MEMBER,
  ],
  reports: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
  ],
  resource_management: [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MANAGER,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.MEMBER,
  ],
};

export const ALL_PAGES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "tickets", label: "Tickets" },
  { key: "board", label: "Sprint Board" },
  { key: "employees", label: "Employees" },
  { key: "departments", label: "Departments" },
  { key: "projects", label: "Projects" },
  { key: "reports", label: "Reports" },
  { key: "resource_management", label: "Resource Management" },
  { key: "role_management", label: "Role Management" },
];

export const ACCESS_LEVELS = { READ: "read", WRITE: "write" };
export const DATA_SCOPES = { OWN: "own", ALL: "all" };

// Populated once at app start from GET /api/roles (see UserRoleContext), so
// custom roles created via Role Management get real page access without
// touching every call site that already imports canAccessPage/PAGE_ACCESS.
// Shape: { [role_name]: { [page_key]: { access_level, data_scope } } }
let dynamicPageAccess = null;

export const setDynamicPageAccess = (rolesFromApi) => {
  if (!Array.isArray(rolesFromApi)) {
    return;
  }

  dynamicPageAccess = rolesFromApi.reduce((map, role) => {
    map[role.role_name] = (role.pages || []).reduce((pageMap, page) => {
      pageMap[page.page_key] = {
        access_level: page.access_level || ACCESS_LEVELS.WRITE,
        data_scope: page.data_scope || DATA_SCOPES.ALL,
      };
      return pageMap;
    }, {});
    return map;
  }, {});
};

export const normalizeRole = (roleName) => {
  if (!roleName) {
    return USER_ROLES.MEMBER;
  }

  return String(roleName).trim().toLowerCase();
};

export const canAccessPage = (roleName, pageKey) => {
  const normalizedRole = normalizeRole(roleName);

  if (dynamicPageAccess && normalizedRole in dynamicPageAccess) {
    return pageKey in dynamicPageAccess[normalizedRole];
  }

  const allowedRoles = PAGE_ACCESS[pageKey] || [];
  return allowedRoles.includes(normalizedRole);
};

// Falls back to full write/all access for roles/pages not covered by the
// dynamic map yet (e.g. before it loads), matching prior hardcoded behavior.
export const getPagePermission = (roleName, pageKey) => {
  const normalizedRole = normalizeRole(roleName);
  const dynamicEntry = dynamicPageAccess?.[normalizedRole]?.[pageKey];

  if (dynamicEntry) {
    return dynamicEntry;
  }

  return { access_level: ACCESS_LEVELS.WRITE, data_scope: DATA_SCOPES.ALL };
};

export const canWritePage = (roleName, pageKey) =>
  getPagePermission(roleName, pageKey).access_level === ACCESS_LEVELS.WRITE;

export const hasOwnDataScope = (roleName, pageKey) =>
  getPagePermission(roleName, pageKey).data_scope === DATA_SCOPES.OWN;

